import { useCallback } from 'react';
import {
  composeValidations,
  pathAvailableValidation,
  useValidation,
} from './common.js';

const PotentialCustomElementNameRegEx =
  //eslint-disable-next-line no-misleading-character-class
  /^[a-z](?:[.0-9_a-z\xB7\xC0-\xD6\xD8-\xF6\xF8-\u037D\u037F-\u1FFF\u200C\u200D\u203F\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]|[\uD800-\uDB7F][\uDC00-\uDFFF])*-(?:[\x2D.0-9_a-z\xB7\xC0-\xD6\xD8-\xF6\xF8-\u037D\u037F-\u1FFF\u200C\u200D\u203F\u2040\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]|[\uD800-\uDB7F][\uDC00-\uDFFF])*$/;

const isPotentialCustomElementName = function (string: string) {
  return PotentialCustomElementNameRegEx.test(string);
};

const reservedNames = new Set([
  'annotation-xml',
  'color-profile',
  'font-face',
  'font-face-src',
  'font-face-uri',
  'font-face-format',
  'font-face-name',
  'missing-glyph',
]);

export function createCustomElementFolderValidation(projectFolder: string) {
  return composeValidations<string>([
    [
      pathAvailableValidation(projectFolder),
      'The element folder must be unique. Select a name that has not been used yet in your application.',
    ],
    [(name) => Boolean(name), `Missing element name.`],
    [
      (name) => !/[A-Z]/.test(name),
      `Element names must not contain uppercase ASCII characters.`,
    ],
    [
      (name) => name.includes('-'),
      `Element names must contain a hyphen. Example: unicorn-cake`,
    ],
    [
      (name) => !/^\d/i.test(name),
      `Element names must not start with a digit.`,
    ],
    [
      (name) => !/^-/i.test(name),
      `Element names must not start with a hyphen.`,
    ],
    [(name) => isPotentialCustomElementName(name), 'Invalid element name.'],
    [
      (name) => !reservedNames.has(name),
      "The supplied element name is reserved and can't be used.\nSee: https://html.spec.whatwg.org/multipage/scripting.html#valid-custom-element-name",
    ],
  ]);
}

export const useCustomElementNameValidation = () => {
  const validation = useValidation(
    composeValidations([
      [
        (name: string) => name.length <= 30,
        'Enter a name that is shorter than 30 characters.',
      ],
    ])
  );

  return useCallback(validation, [validation]);
};
